import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import { ordersAPI } from '../services/api';
import { textures } from '../data/products';
import '../App.css';
import './Confirmation.css';

const Confirmation = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.pageYOffset > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await ordersAPI.getByOrderId(orderId);
        if (response.success && response.data) {
          const orderData = {
            orderId: response.data.order_id,
            date: response.data.created_at,
            status: response.data.status,
            total: parseFloat(response.data.total) || 0,
            items: response.data.items || [],
            customer: {
              firstName: response.data.customer_first_name,
              lastName: response.data.customer_last_name,
              email: response.data.customer_email,
              address: response.data.customer_address,
              city: response.data.customer_city,
              phone: response.data.customer_phone
            },
            discountCode: response.data.discount_code,
            discountAmount: response.data.discount_amount,
            downloadToken: response.data.downloadToken,
            downloadPassword: response.data.downloadPassword,
            downloadExpiresAt: response.data.downloadExpiresAt
          };
          setOrder(orderData);
        } else {
          const savedOrder = sessionStorage.getItem('vague_last_order');
          if (savedOrder) {
            const orderData = JSON.parse(savedOrder);
            if (orderData.order_id === orderId || orderData.orderId === orderId) {
              setOrder(orderData);
            } else {
              navigate('/shop');
            }
          } else {
            navigate('/shop');
          }
        }
      } catch (error) {
        console.error('Error loading order:', error);
        try {
          const savedOrder = sessionStorage.getItem('vague_last_order');
          if (savedOrder) {
            const orderData = JSON.parse(savedOrder);
            if (orderData.order_id === orderId || orderData.orderId === orderId) {
              setOrder(orderData);
            } else {
              navigate('/shop');
            }
          } else {
            navigate('/shop');
          }
        } catch (e) {
          navigate('/shop');
        }
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    }
  }, [orderId, navigate]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return '#4caf50';
      case 'processing':
        return '#ff9800';
      case 'shipped':
        return '#2196f3';
      case 'delivered':
        return '#4caf50';
      default:
        return '#8b0000';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const menuItems = [
    { type: 'link', label: 'Home', to: '/' },
    { type: 'link', label: 'Vague', to: '/' },
    { type: 'link', label: 'Shop', to: '/shop' }
  ];

  if (loading) {
    return (
      <div className="confirmation-page">
        <ResponsiveNavbar
          menuItems={menuItems}
          logo="VELOURA"
          logoLink="/"
          scrolled={scrolled}
        />
        <div className="confirmation-container">
          <div className="loading-message">Loading order details...</div>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
      className="confirmation-page"
    >
      {}
      <ResponsiveNavbar
        menuItems={menuItems}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />
      <div className="confirmation-container">
        {}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="confirmation-header"
        >
          <div className="confirmation-icon">✓</div>

          <h1 className="confirmation-title">Order Confirmed</h1>
          <p className="confirmation-subtitle">
            Thank you for your purchase! We've received your order and will begin processing it shortly.
          </p>
        </motion.div>
        <div className="confirmation-content">
          <div className="confirmation-section">
            <h2 className="confirmation-section-title">Order Details</h2>
            <div className="confirmation-info-grid">
              <div className="confirmation-info-item">
                <span className="info-label">Order ID</span>
                <span className="info-value">{order.orderId || order.order_id}</span>
              </div>
              <div className="confirmation-info-item">
                <span className="info-label">Date</span>
                <span className="info-value">{formatDate(order.date || order.created_at)}</span>
              </div>
              <div className="confirmation-info-item">
                <span className="info-label">Status</span>
                <span
                  className="info-value status-badge"
                  style={{ color: getStatusColor(order.status) }}
                >
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
              </div>
              {order.discountCode && order.discountAmount && (
                <>
                  <div className="confirmation-info-item">
                    <span className="info-label">Discount Code</span>
                    <span className="info-value">{order.discountCode}</span>
                  </div>
                  <div className="confirmation-info-item">
                    <span className="info-label">Discount</span>
                    <span className="info-value" style={{ color: '#32cd32' }}>-${parseFloat(order.discountAmount).toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="confirmation-info-item">
                <span className="info-label">Total</span>
                <span className="info-value total-amount">${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div className="confirmation-section">
            <h2 className="confirmation-section-title">Items Ordered</h2>
            <div className="confirmation-items">
              {order.items.map((item, index) => (
                <motion.div
                  key={`${item.productId}-${item.texture}-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="confirmation-item"
                >
                  <img
                    src={item.productImage}
                    alt={item.productName}
                    className="confirmation-item-image"
                  />
                  <div className="confirmation-item-info">
                    <h4 className="confirmation-item-name">{item.productName}</h4>
                    <p className="confirmation-item-texture">
                      {textures[item.texture]?.name || item.texture}
                    </p>
                    <p className="confirmation-item-quantity">Quantity: {item.quantity}</p>
                  </div>
                  <div className="confirmation-item-price">
                    ${((parseFloat(item.price) || 0) * item.quantity).toFixed(2)}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="confirmation-section">
            <h2 className="confirmation-section-title">Shipping Information</h2>
            <div className="confirmation-shipping-info">
              <p className="shipping-name">
                {order.customer.firstName} {order.customer.lastName}
              </p>
              <p className="shipping-address">{order.customer.address}</p>
              <p className="shipping-location">{order.customer.city}</p>
              {order.customer.phone && (
                <p className="shipping-phone">Phone: {order.customer.phone}</p>
              )}
              <p className="shipping-email">Email: {order.customer.email}</p>
            </div>
          </div>
          {order.status === 'pending' && (
            <div className="confirmation-section">
              <h2 className="confirmation-section-title">Complete Payment</h2>
              <p className="confirmation-subtitle" style={{ marginBottom: '1rem', color: '#d32f2f' }}>
                Your order has not been paid yet. To finalize the payment for this order, please{' '}
                <a
                  href={(() => {
                    const fleecaApiKey = process.env.REACT_APP_FLEECA_API_KEY || '2V6iKuuW4aifBDvuuPJn6yphQLqde0seKfXdjHH6UjQZudr5eNLNJk5t6syqK807';
                    const fleecaGatewayUrl = process.env.REACT_APP_FLEECA_GATEWAY_URL || 'http://localhost:3000'
                    const orderTotal = order.total || 0;
                    return `${fleecaGatewayUrl}${fleecaApiKey}/0/${orderTotal}`;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#d32f2f', textDecoration: 'underline', fontWeight: 600 }}
                >
                  click here
                </a>.
              </p>
            </div>
          )}
          {}
          {order.downloadToken && (
            <div className="confirmation-section">
              <h2 className="confirmation-section-title">Download Your Mods</h2>
              <p className="confirmation-subtitle" style={{ marginBottom: '1rem' }}>
                Your mod files are ready for download. Click below to access your purchase.
              </p>
              <Link
                to={`/purchase-complete/${order.orderId || order.order_id}`}
                className="confirmation-button primary"
                style={{ display: 'inline-block', textDecoration: 'none' }}
              >
                Go to Download Page
              </Link>
            </div>
          )}
          {}
          <div className="confirmation-actions">
            <Link to="/shop" className="confirmation-button secondary">
              Continue Shopping
            </Link>

            <button
              onClick={() => window.print()}
              className="confirmation-button primary"
            >
              Print Order
            </button>

          </div>

        </div>

      </div>

    </motion.div>

  );
};

export default Confirmation;

